import { createTheme } from "@material-ui/core/styles";
import { ptBR } from "@material-ui/core/locale";

// Gxon Color Palette
const gxonColors = {
    primary: {
        main: "#316AFF",
        light: "#5A8AFF",
        dark: "#1E4ACC",
        contrastText: "#FFFFFF",
    },
    success: {
        main: "#10B981",
        light: "#34D399",
        dark: "#059669",
        contrastText: "#FFFFFF",
    },
    warning: {
        main: "#F59E0B",
        light: "#FBBF24",
        dark: "#D97706",
        contrastText: "#FFFFFF",
    },
    error: {
        main: "#EF4444",
        light: "#F87171",
        dark: "#DC2626",
        contrastText: "#FFFFFF",
    },
    info: {
        main: "#3B82F6",
        light: "#60A5FA",
        dark: "#2563EB",
        contrastText: "#FFFFFF",
    },
    background: {
        default: "#FDFDFD",
        paper: "#FFFFFF",
        dark: "#F9FAFB",
    },
    text: {
        primary: "#1F2937",
        secondary: "#6B7280",
        disabled: "#9CA3AF",
        hint: "#D1D5DB",
    },
    divider: "#E5E7EB",
};

// Create theme for light mode
const createGxonTheme = (mode = "light") => {
    const isLight = mode === "light";

    return createTheme(
        {
            palette: {
                mode: mode,
                primary: gxonColors.primary,
                secondary: gxonColors.primary, // Using primary as secondary for consistency
                success: gxonColors.success,
                warning: gxonColors.warning,
                error: gxonColors.error,
                info: gxonColors.info,
                background: isLight
                    ? gxonColors.background
                    : {
                        default: "#111827",
                        paper: "#1F2937",
                        dark: "#0F172A",
                    },
                text: isLight
                    ? gxonColors.text
                    : {
                        primary: "#F9FAFB",
                        secondary: "#D1D5DB",
                        disabled: "#9CA3AF",
                        hint: "#6B7280",
                    },
                divider: isLight ? gxonColors.divider : "#374151",
            },
            typography: {
                fontFamily: [
                    '"Plus Jakarta Sans"',
                    '"Inter"',
                    '"Roboto"',
                    '"Helvetica"',
                    '"Arial"',
                    'sans-serif',
                ].join(','),
                fontSize: 14,
                fontWeightLight: 300,
                fontWeightRegular: 400,
                fontWeightMedium: 500,
                fontWeightBold: 700,
                h1: {
                    fontSize: "2.5rem",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    letterSpacing: "-0.01562em",
                },
                h2: {
                    fontSize: "2rem",
                    fontWeight: 700,
                    lineHeight: 1.3,
                    letterSpacing: "-0.00833em",
                },
                h3: {
                    fontSize: "1.75rem",
                    fontWeight: 600,
                    lineHeight: 1.4,
                },
                h4: {
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    lineHeight: 1.4,
                },
                h5: {
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    lineHeight: 1.5,
                },
                h6: {
                    fontSize: "1rem",
                    fontWeight: 600,
                    lineHeight: 1.5,
                },
                subtitle1: {
                    fontSize: "1rem",
                    fontWeight: 500,
                    lineHeight: 1.75,
                },
                subtitle2: {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    lineHeight: 1.57,
                },
                body1: {
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    lineHeight: 1.5,
                },
                body2: {
                    fontSize: "0.8125rem",
                    fontWeight: 400,
                    lineHeight: 1.43,
                },
                button: {
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    lineHeight: 1.75,
                    textTransform: "none", // Gxon doesn't use uppercase buttons
                },
                caption: {
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    lineHeight: 1.66,
                },
                overline: {
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    lineHeight: 2.66,
                    textTransform: "uppercase",
                },
            },
            shape: {
                borderRadius: 10, // Gxon uses 10px for cards
            },
            spacing: 8, // Base spacing unit
            shadows: [
                "none",
                "0px 1px 2px rgba(0, 0, 0, 0.05)",
                "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
                "0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)",
                "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
                "0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)",
                "0px 25px 50px -12px rgba(0, 0, 0, 0.25)",
                ...Array(18).fill("none"), // Fill remaining shadow levels
            ],
            overrides: {
                MuiCssBaseline: {
                    "@global": {
                        "*": {
                            boxSizing: "border-box",
                        },
                        html: {
                            WebkitFontSmoothing: "antialiased",
                            MozOsxFontSmoothing: "grayscale",
                        },
                        body: {
                            margin: 0,
                            padding: 0,
                        },
                    },
                },
                MuiButton: {
                    root: {
                        borderRadius: 8, // Gxon uses 8px for buttons
                        padding: "10px 20px",
                        fontWeight: 600,
                        textTransform: "none",
                        boxShadow: "none",
                        "&:hover": {
                            boxShadow: "none",
                        },
                    },
                    contained: {
                        boxShadow: "none",
                        "&:hover": {
                            boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        },
                    },
                    containedPrimary: {
                        backgroundColor: gxonColors.primary.main,
                        "&:hover": {
                            backgroundColor: gxonColors.primary.dark,
                        },
                    },
                },
                MuiCard: {
                    root: {
                        borderRadius: 10,
                        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
                        border: `1px solid ${isLight ? "#F3F4F6" : "#374151"}`,
                    },
                },
                MuiPaper: {
                    rounded: {
                        borderRadius: 10,
                    },
                    elevation1: {
                        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
                    },
                },
                MuiTextField: {
                    root: {
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 8,
                            "& fieldset": {
                                borderColor: isLight ? "#E5E7EB" : "#374151",
                            },
                            "&:hover fieldset": {
                                borderColor: isLight ? "#D1D5DB" : "#4B5563",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: gxonColors.primary.main,
                                borderWidth: 2,
                            },
                        },
                    },
                },
                MuiTableCell: {
                    root: {
                        borderBottom: `1px solid ${isLight ? "#F3F4F6" : "#374151"}`,
                        padding: "12px 16px",
                    },
                    head: {
                        fontWeight: 600,
                        color: isLight ? gxonColors.text.secondary : "#D1D5DB",
                        backgroundColor: isLight ? "#F9FAFB" : "#1F2937",
                    },
                },
                MuiChip: {
                    root: {
                        borderRadius: 6,
                        fontWeight: 500,
                        fontSize: "0.75rem",
                    },
                },
                MuiDrawer: {
                    paper: {
                        borderRight: "none",
                        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                    },
                },
                MuiAppBar: {
                    root: {
                        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    },
                },
            },
            // Custom scrollbar styles
            scrollbarStyles: {
                "&::-webkit-scrollbar": {
                    width: "8px",
                    height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                    backgroundColor: isLight ? "#F3F4F6" : "#1F2937",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: isLight ? "#D1D5DB" : "#4B5563",
                    borderRadius: "4px",
                    "&:hover": {
                        backgroundColor: isLight ? "#9CA3AF" : "#6B7280",
                    },
                },
            },
        },
        ptBR
    );
};

export default createGxonTheme;
export { gxonColors };
